import { ACADEMIC_PERIODS_NAMES, TRANSCRIPTS_CATEGORIES } from '../constants';
import { SubjectAreaNames } from './EOAInterface';

type CategoriesWithValues<T> = {
  RIGOROUS_ACADEMIC: T;
  ON_THE_CUSP: T;
  STRUGGLING: T;
};

type TranscriptSelectionHeaders = {
  link: string,
  'x-total-count': string
};
interface TranscriptSelectionSummary {
  headers: TranscriptSelectionHeaders,
  has_next_batch: boolean,
  is_finalized: boolean,
  selection_counts: CategoriesWithValues<number>,
  transcripts: { [key in keyof typeof TRANSCRIPTS_CATEGORIES]: Transcript[]; }
}

interface SelectedTranscriptsSummary {
  is_finalized: boolean;
  selection_counts: {
    [key: string]: number;
  };
  selectedTranscriptsByCategory: {
    [key: string]: Transcript[];
  };
}

interface Transcript {
  id: string,
  school_student_number: number,
  category: string,
  is_selected: boolean,
  gpa: number,
  credits: number,
  race: string,
  gender: string,
  student_with_disabilities: string,
  english_learner: string,
  low_income: string,
  course_intensity_score: number,
  profile_names: string[]
}

interface Course {
  name: string;
  code: string;
  letter_grade: string | null;
  number_grade: number | null;
  num_credits: number;
}

interface SubjectArea {
  name: string;
  num_credits: number;
  courses: Course[];
}

interface Term {
  name: string;
  subject_areas: SubjectArea[]
}

interface AcademicPeriod {
  period: string;
  terms: Term[]
}

interface TranscriptDetails {
  id: string;
  school_student_number: number;
  category: string;
  is_selected: boolean;
  gpa: number;
  credits: number;
  race: string;
  gender: string;
  student_with_disabilities: string;
  english_learner: string;
  low_income: string;
  academic_periods: AcademicPeriod[];
  course_intensity_score?: number;
  profile_names?: string[];
}

type OmittedDetails = 'course_intensity_score' | 'profile_names';
type FinalizedTranscriptDetails = Omit<TranscriptDetails, OmittedDetails>;

interface FinalizedTranscriptDetailsResponse {
  is_finalized: boolean;
  transcripts: {
    [key in keyof typeof TRANSCRIPTS_CATEGORIES]: FinalizedTranscriptDetails[];
  };
};

type AcademicPeriodNames = keyof typeof ACADEMIC_PERIODS_NAMES;

type AcademicPeriodData = {
  [ACADEMIC_PERIODS_NAMES.NINTH_GRADE]: Course[],
  [ACADEMIC_PERIODS_NAMES.TENTH_GRADE]: Course[],
  [ACADEMIC_PERIODS_NAMES.ELEVENTH_GRADE]: Course[],
  [ACADEMIC_PERIODS_NAMES.TWELFTH_GRADE]: Course[],
  studentNumber: number;
};

type TranscriptsBySubjectAreaType = {
  [key in SubjectAreaNames]: AcademicPeriodData[];
};

interface Category {
  name: string;
  label: string;
}

type TranscripsCategoriesKeys = keyof typeof TRANSCRIPTS_CATEGORIES;
type TranscripsCategories = {
  [key in TranscripsCategoriesKeys]: Transcript[]
};
type SelectedTranscriptsIdsByCategory = {
  [key in TranscripsCategoriesKeys]: string[]
};

interface CategorizedSelectedTranscriptsIds {
  rigorous_academic_transcript_ids: string[];
  on_the_cusp_transcript_ids: string[];
  struggling_transcript_ids: string[];
};

export enum ChartCategoryTypes {
  RACE = 'race',
  GENDER = 'gender',
  STUDENT_WITH_DISABILITIES = 'student_with_disabilities',
  ENGLISH_LEARNER = 'english_learner',
  LOW_INCOME = 'low_income'
}

export enum DemographicChartLabels {
  'WITH DISABILITIES' = 'Students with disabilities',
  'ENGLISH LANGUAGE LEARNER' = 'English language learners',
  'LOW-INCOME' = 'Low income students'
};

interface TranscriptPrintError {
  type: 'error';
  studentNumber: number;
}

interface TranscriptPrintData {
  type: 'data';
  data: TranscriptDetails;
}


export type {
  CategoriesWithValues,
  TranscriptSelectionSummary,
  SelectedTranscriptsSummary,
  Transcript,
  TranscriptDetails,
  FinalizedTranscriptDetails,
  FinalizedTranscriptDetailsResponse,
  TranscriptsBySubjectAreaType,
  AcademicPeriodData,
  AcademicPeriodNames,
  Category,
  Term,
  AcademicPeriod,
  SubjectArea,
  Course,
  TranscripsCategories,
  TranscripsCategoriesKeys,
  SelectedTranscriptsIdsByCategory,
  CategorizedSelectedTranscriptsIds,
  TranscriptPrintError,
  TranscriptPrintData
};
